import React, { useState } from "react";
import { Link2, Copy, Power } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SHARE_ROLES } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const SharePanel = ({ sh, onSaved }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!SHARE_ROLES.includes(user?.role)) return null;
  const url = sh.share_token ? `${window.location.origin}/track/${sh.share_token}` : "";
  const on = !!sh.share_enabled;

  const toggle = async () => {
    setBusy(true);
    try {
      await api.post(`/shipments/${sh.id}/share`, { enabled: !on });
      toast.success(on ? "Client link turned off" : "Client link is live");
      onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select the link and copy it manually"); }
  };

  return (
    <div data-testid="share-panel" className="glass-card rounded-lg p-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 font-display text-xl font-bold">
          <Link2 size={18} className="text-sky-400" /> Client status link
        </div>
        <span data-testid="share-status" className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
          on ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-slate-800 text-slate-400 border-slate-700"}`}>
          {on ? "Live" : "Off"}
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Clients see only the shipment number, current stage and last update. No prices, documents or staff names.
      </p>
      {on && (
        <div className="flex gap-2 mb-3">
          <Input data-testid="share-url" readOnly value={url} onFocus={(e) => e.target.select()}
                 className="bg-slate-950 border-slate-700 font-mono-ops text-xs" />
          <Button data-testid="button-copy-share" variant="outline" onClick={copy} className="border-slate-700 hover:bg-slate-800">
            <Copy size={14} />
          </Button>
        </div>
      )}
      <Button data-testid="button-toggle-share" onClick={toggle} disabled={busy}
              className={on ? "bg-slate-800 hover:bg-slate-700 border border-slate-700" : "bg-blue-600 hover:bg-blue-500"}>
        <Power size={14} className="mr-2" /> {on ? "Turn off link" : "Create / turn on link"}
      </Button>
    </div>
  );
};
